import { useEffect, useMemo, useRef, useState } from 'react';
import { getProductLines } from '../services/ClientService';
import { getCreditoMaximo } from '../services/RiskService';
import { getQuotations, Quotation } from '../services/QuotationsService';
import type { applicationData } from '../types/interfaces';
import { isSuccessfulResponse } from '../utils/Utils';
import type { CreditoMaximoState, ProductoOtorgado } from '../organisms/Risk/CreditoMaximo/CreditoMaximo.types';
import {
  hasCreditoMaximoUnitSale,
  mapSaleValueFromCreditoMaximoApi,
} from '../utils/creditoMaximoMonto';
import {
  computeSuperaMaximo,
  resolveQuotationValues,
} from '../utils/creditoMaximoSupera';
import { parseSelectableProductLines, type SelectableProductLine } from '../utils/productLines';

const initProductosFromApplicationData = (products: applicationData[]): ProductoOtorgado[] =>
  products.map((p) => ({
    product_id: p.id,
    product_line_code: p.product_line_code,
    asset_code: p.asset_code,
    asset_name: p.asset_name,
    cantidad_otorgada: p.asset_count?.toString() ?? '0',
    monto_otorgado: p.amount?.value?.toString() ?? '0',
    currency_code: p.amount?.currency_code ?? 'ARS',
  }));

const mapProductoFromApi = (p: Record<string, unknown>): ProductoOtorgado => {
  const sv = mapSaleValueFromCreditoMaximoApi(p);
  return {
    product_id: Number(p.productId ?? p.product_id ?? 0),
    product_line_code: String(p.productLineCode ?? p.product_line_code ?? ''),
    asset_code: p.assetCode != null ? String(p.assetCode) : p.asset_code != null ? String(p.asset_code) : undefined,
    asset_name: p.assetName != null ? String(p.assetName) : p.asset_name != null ? String(p.asset_name) : undefined,
    cantidad_otorgada: String(p.cantidadOtorgada ?? p.cantidad_otorgada ?? '0'),
    monto_otorgado: String(p.montoOtorgado ?? p.monto_otorgado ?? '0'),
    currency_code: String(p.currencyCode ?? p.currency_code ?? 'ARS'),
    ...(sv != null ? { saleValue: sv } : {}),
  };
};

const EMPTY: CreditoMaximoState = {
  activo_fijo: 0,
  revaluos: '0',
  restricciones: '0',
  activo_fijo_libre: 0,
  credito_maximo_unidades: 0,
  credito_maximo_pesos: 0,
  calificacion: null,
  productos: [],
  superaMaximo: false,
  unidad_calculo_code: 'BOL',
};

/**
 * Estado de crédito máximo / monto a otorgar para vistas solo lectura en Comité.
 *
 * @param applicationReady — Si es false, no se llama a getCreditoMaximo (evita GET antes de tener `products` de la solicitud y duplica con refreshKey).
 */
export function useCommitteeCreditoMaximo(
  cuit: string | undefined,
  applicationId: string | undefined,
  enabled: boolean,
  applicationProducts: applicationData[],
  isQuotationsEnabled: boolean = false,
  /** Incrementar tras recargar la solicitud o un registro de aprobación en comité para volver a pedir getCreditoMaximo. */
  refreshKey: number = 0,
  applicationReady: boolean = true
) {
  const [state, setState] = useState<CreditoMaximoState | null>(null);
  const [productLines, setProductLines] = useState<SelectableProductLine[]>([]);
  const [availableAssets, setAvailableAssets] = useState<Array<{ code: string; name: string; saleValue?: number | string }>>([]);
  const [loading, setLoading] = useState(false);
  const productsRef = useRef(applicationProducts);
  productsRef.current = applicationProducts;

  useEffect(() => {
    getProductLines().then((res) => {
      if (res && 'data' in res) setProductLines(parseSelectableProductLines(res.data));
    });
  }, []);

  useEffect(() => {
    if (!isQuotationsEnabled) return;
    getQuotations()
      .then((res) => {
        const assets: Array<{ code: string; name: string; saleValue?: number | string }> =
          Array.isArray(res?.data)
            ? res.data.map((q: Quotation) => ({ code: q.code, name: q.name, saleValue: q.saleValue }))
            : [];
        setAvailableAssets(assets);
      })
      .catch(() => setAvailableAssets([]));
  }, [isQuotationsEnabled]);

  useEffect(() => {
    if (!enabled || !cuit || !applicationId) {
      setState(null);
      setLoading(false);
      return;
    }
    if (!applicationReady) {
      setState(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    getCreditoMaximo(cuit, applicationId)
      .then((res) => {
        if (cancelled) return;
        if (isSuccessfulResponse(res) && res.data) {
          const data = res.data as Record<string, unknown>;
          const productosRaw = data.productos as Record<string, unknown>[] | undefined;
          setState({
            activo_fijo: Number(data.activoFijo ?? data.activo_fijo ?? 0),
            revaluos: String(data.revaluos ?? '0'),
            restricciones: String(data.restricciones ?? '0'),
            activo_fijo_libre: Number(data.activoFijoLibre ?? data.activo_fijo_libre ?? 0),
            // Lectura defensiva: nuevo nombre primero, fallback al nombre anterior (compatibilidad 1 release)
            credito_maximo_unidades: Number(data.creditoMaximoUnidades ?? data.creditoMaximoBolsas ?? data.credito_maximo_unidades ?? data.credito_maximo_bolsas ?? 0),
            credito_maximo_pesos: Number(data.creditoMaximoPesos ?? data.credito_maximo_pesos ?? 0),
            calificacion: (data.calificacion as string | null | undefined) ?? null,
            superaMaximo: data.superaMaximo === true || data.supera_maximo === true,
            productos:
              productosRaw && productosRaw.length > 0
                ? productosRaw.map(mapProductoFromApi)
                : initProductosFromApplicationData(productsRef.current),
            unidad_calculo_code: String(data.unidadCalculoCode ?? data.unidad_calculo_code ?? 'BOL'),
          });
        } else {
          setState({
            ...EMPTY,
            productos: initProductosFromApplicationData(productsRef.current),
          });
        }
      })
      .catch(() => {
        if (cancelled) return;
        setState({
          ...EMPTY,
          productos: initProductosFromApplicationData(productsRef.current),
        });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, cuit, applicationId, refreshKey, applicationReady]);

  const mergedAvailableAssets = useMemo(() => {
    const productos = state?.productos ?? [];
    const map = new Map<string, { code: string; name: string; saleValue?: number | string }>();
    for (const a of availableAssets) map.set(a.code, { ...a });
    for (const p of productos) {
      if (p.asset_code && p.saleValue != null && p.saleValue > 0) {
        const cur = map.get(p.asset_code);
        if (!cur || !hasCreditoMaximoUnitSale(cur.saleValue)) {
          map.set(p.asset_code, {
            code: p.asset_code,
            name: p.asset_name ?? p.asset_code,
            saleValue: p.saleValue,
          });
        }
      }
    }
    return Array.from(map.values());
  }, [availableAssets, state?.productos]);

  const superaMaximo = useMemo(() => {
    if (!state) return false;
    const { valorBolsa, valorM3 } = resolveQuotationValues(mergedAvailableAssets);
    return computeSuperaMaximo({
      productos: state.productos,
      creditoMaximoUnidades: state.credito_maximo_unidades,
      unidadCalculoCode: state.unidad_calculo_code ?? 'BOL',
      valorBolsa,
      valorM3,
    });
  }, [state, mergedAvailableAssets]);

  return { state, productLines, availableAssets: mergedAvailableAssets, loading, superaMaximo };
}
